import { IconNames } from '@blueprintjs/icons';
import { formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { type ColumnDef, GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { PageTitle } from '@strata/react-page-title';
import { Icon } from '@strata/react-status-icons';
import { type ComponentProps, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { DeleteListDialog } from 'components/admin/dialogs/DeleteListDialog';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { type PrescriberList, useGetPrescriberListsQuery } from 'store/api/auctionApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';

const customSearchValueHandlers: ComponentProps<
    typeof GridWithSearch<PrescriberList>
>['customSearchValueHandlers'] = {
    prescribers: (prescribers) =>
        prescribers?.map((prescriber) => `${prescriber.npi}${prescriber.name}`).join(' '),
};

export const PrescriberLists = () => {
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const loggedInSupplierName = useLoggedInSupplierName();

    const { data: prescriberLists, isFetching, refetch } = useGetPrescriberListsQuery();

    const [prescriberListToDelete, setPrescriberListToDelete] = useState<PrescriberList>();

    const columns = useMemo<ColumnDef<PrescriberList>[]>(
        () => [
            {
                accessorKey: 'listName',
                header: 'Prescriber List Name',
                cell: ({ getValue }) => <PrimaryData data={getValue()} />,
            },
            {
                accessorKey: 'lastUpdated',
                header: 'Last Updated',
                cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
            },
            {
                accessorKey: 'updatedBy',
                header: 'Updated By',
            },
            {
                accessorKey: 'prescriberListId',
                header: 'Actions',
                enableSorting: false,
                size: 70,
                cell: ({ getValue, row }) => (
                    <div className="sds_grid-actions-row">
                        <Icon
                            icon={IconNames.EDIT}
                            intent={Icon.Intent.SECONDARY}
                            title="Edit"
                            onClick={() => navigate(AppRoutes.prescriberListDetails(getValue()))}
                        />
                        {userCanEdit && (
                            <Icon
                                icon={IconNames.TRASH}
                                intent={Icon.Intent.SECONDARY}
                                title="Delete"
                                onClick={() => setPrescriberListToDelete(row.original)}
                            />
                        )}
                    </div>
                ),
            },
        ],
        [userCanEdit, navigate],
    );

    return (
        <FormPageContainer>
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <PageTitle title="Prescriber Lists" secondaryTitle={loggedInSupplierName} />
                <DeleteListDialog
                    listType={DeleteListDialog.ListType.PRESCRIBER}
                    listToDelete={prescriberListToDelete}
                    onClose={() => setPrescriberListToDelete(undefined)}
                    onDeleteSuccess={() => {
                        void refetch();
                    }}
                />
                <div style={{ overflow: 'auto', flexGrow: 1 }}>
                    <GridWithSearch
                        data={prescriberLists}
                        isLoading={isFetching}
                        sortProperties={[{ id: 'listName', desc: false }]}
                        customSearchValueHandlers={customSearchValueHandlers}
                        rightHeaderContent={
                            <Button
                                label="Add Prescriber List"
                                id="add-prescriber-list-button"
                                intent={Button.Intent.PRIMARY}
                                disabled={!userCanEdit}
                                onClick={() => navigate(AppRoutes.prescriberListDetails('create'))}
                            />
                        }
                        columns={columns}
                    />
                </div>
            </div>
        </FormPageContainer>
    );
};
